import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { useEffect, useRef } from 'react';
import { getJitteredInterval } from 'utils/pollingUtility';
import React from 'react';
import { createRoot } from 'react-dom/client';
import PrintableTable from './components/PrintableTable';
import {toast} from 'react-toastify';

export const useStagingPriorityList = () => {
  const { StagingPriorityStore } = useStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  useEffect(() => {
    StagingPriorityStore.fetchStagingPriorityData();
  }, []);

  // Polling function
  const pollData = async () => {
    try {
      await StagingPriorityStore.pollStagingPriorityData();
    } catch (error) {
      // Don't break polling cycle on error
      if (environment === 'development') {
        console.error('Polling error:', error);
      }
      toast.error('Error during polling for Staging Priority List data!');
    }
  };

  // Start polling after initial load
  useInterval(pollData, POLLING_INTERVAL);

  const getDate = () => {
    const date = new Date();
    const month = date.getMonth() + 1; // getMonth is 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}-${year}`;
  };

  const getDateTime = () => {
    const date = new Date();
    const month = date.getMonth() + 1; // getMonth is 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Format time in 12-hour format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}-${year} ${displayHours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds} ${ampm}`;
  };

  const handlePrint = () => {
    // Open print window
    const printWindow = window.open('', '', 'width=1200,height=800');
    if (!printWindow) {
      alert('Could not open print window!');
      return;
    }

    // Set up the basic HTML structure
    printWindow.document.write(`
      <html>
        <head>
          <title>Staging Priority List</title>
          <meta charset="utf-8">
        </head>
        <body>
          <div id="print-root"></div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Get the container div
    const printContainer = printWindow.document.getElementById('print-root');
    if (!printContainer) {
      alert('Could not find print container!');
      return;
    }

    // Create React root and render the PrintableTable component
    const root = createRoot(printContainer);
    root.render(
      React.createElement(PrintableTable, {
        data: StagingPriorityStore.stagingPriorityData,
        dateTime: getDateTime()
      })
    );

    // Wait for rendering to complete, then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 1000);
  };

  return {
    isLoading: StagingPriorityStore.isLoading,
    stagingPriorityData: StagingPriorityStore.stagingPriorityData,
    contentRef,
    getDate,
    getDateTime,
    handlePrint
  };
};
